import { useEffect } from 'react';

import workdayFaviconBlueSrc from '~/ui/assets/images/workday-favicon-blue.webp';
import workdayFaviconWhiteSrc from '~/ui/assets/images/workday-favicon-white.webp';

import { useIsSidebarNavAvailable } from './useIsSidebarNavAvailable';

const FAVICON_SELECTOR = 'link[rel~="icon"]';
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

function getFaviconSrc(isDarkMode: boolean) {
  return isDarkMode ? workdayFaviconWhiteSrc : workdayFaviconBlueSrc;
}

/** Swaps the browser tab favicon to the Workday mark (matching the OS color scheme) while `isEnabled`, restoring the original on cleanup. */
export function useSidebarNavFavicon() {
  const isEnabled = useIsSidebarNavAvailable();

  useEffect(() => {
    if (!isEnabled) return undefined;

    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>(FAVICON_SELECTOR),
    );
    const originalHrefs = links.map((link) => link.href);
    const applyFavicon = (isDarkMode: boolean) => {
      const src = getFaviconSrc(isDarkMode);
      links.forEach((link) => {
        link.href = src;
      });
    };

    const darkModeQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(DARK_MODE_QUERY)
        : undefined;
    const handleChange = () => applyFavicon(Boolean(darkModeQuery?.matches));
    applyFavicon(Boolean(darkModeQuery?.matches));
    darkModeQuery?.addEventListener('change', handleChange);

    return () => {
      darkModeQuery?.removeEventListener('change', handleChange);
      links.forEach((link, index) => {
        link.href = originalHrefs[index];
      });
    };
  }, [isEnabled]);
}
